import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
	GitBranch,
	Plus,
	Eye,
	Edit,
	Trash2,
	Archive,
	ToggleLeft,
	ToggleRight,
	Search,
	Filter,
	Loader2,
	AlertCircle,
	CheckCircle,
	XCircle,
	Clock,
} from 'lucide-react'
import { Button, SearchBar, DataTable, Modal, Alert } from '@components/common'
import { useWorkflowStore } from '@store'
import { useAuth } from '@context/AuthContext'
import { ROLES, hasPermission } from '@config/roles'

import './Workflows.scss'

/**
 * Workflows Page
 *
 * Lists all workflows for the company with CRUD operations.
 * Only accessible to Company Admin (Compliance Manager).
 */
const Workflows = ({ userRole = ROLES.COMPANY_ADMIN }) => {
	const navigate = useNavigate()
	const { user } = useAuth()

	// Get company ID from authenticated user
	// FALLBACK: If user is null in AuthContext, try to get from storage directly
	let companyId = user?.company_id

	if (!companyId) {
		try {
			const userDataStr = sessionStorage.getItem('auth_user_data') || localStorage.getItem('auth_user_data')
			if (userDataStr) {
				const userData = JSON.parse(userDataStr)
				companyId = userData?.company_id
				console.warn('⚠️ AuthContext user is null, using company_id from storage:', companyId)
			}
		} catch (error) {
			console.error('Error parsing user data from storage:', error)
		}
	}

	// Store state
	const {
		workflows,
		stats,
		loading,
		error,
		filters,
		fetchWorkflows,
		toggleWorkflow,
		deleteWorkflow,
		setSearch,
		setStatusFilter,
	} = useWorkflowStore()

	// Local state
	const [deleteModalOpen, setDeleteModalOpen] = useState(false)
	const [selectedWorkflow, setSelectedWorkflow] = useState(null)
	const [actionLoading, setActionLoading] = useState(false)

	// Permissions
	const canManage = hasPermission(userRole, 'canManageWorkflows')
	const canDelete = hasPermission(userRole, 'canDeleteWorkflows')

	// Fetch workflows on mount and filter change
	useEffect(() => {
		if (companyId) {
			fetchWorkflows(companyId)
		}
	}, [companyId, filters.status, filters.search])

	// Handle search with debounce
	const handleSearch = useCallback(
		(value) => {
			setSearch(value)
		},
		[setSearch]
	)

	// Handle status filter change
	const handleStatusChange = (e) => {
		setStatusFilter(e.target.value)
	}

	// Handle create new workflow
	const handleCreate = () => {
		navigate('/workflows/new')
	}

	// Handle view workflow
	const handleView = (workflow) => {
		navigate(`/workflows/${workflow.id}`)
	}

	// Handle edit workflow
	const handleEdit = (workflow) => {
		navigate(`/workflows/${workflow.id}/edit`)
	}

	// Handle toggle workflow status
	const handleToggle = async (workflow) => {
		if (workflow.status === 'archived') return
		setActionLoading(true)
		await toggleWorkflow(workflow.id)
		setActionLoading(false)
	}

	// Handle delete/archive workflow
	const handleDeleteClick = (workflow) => {
		setSelectedWorkflow(workflow)
		setDeleteModalOpen(true)
	}

	const handleDeleteConfirm = async () => {
		if (!selectedWorkflow) return
		setActionLoading(true)
		await deleteWorkflow(selectedWorkflow.id)
		setActionLoading(false)
		setDeleteModalOpen(false)
		setSelectedWorkflow(null)
	}

	// Table columns
	const columns = [
		{
			key: 'name',
			label: 'Workflow Name',
			sortable: true,
			render: (row) => (
				<div className="workflow-name-cell">
					<div className="workflow-name-cell__icon">
						<GitBranch size={18} />
					</div>
					<div className="workflow-name-cell__content">
						<span className="workflow-name-cell__name">{row.name}</span>
					</div>
				</div>
			),
		},
		{
			key: 'steps_count',
			label: 'Steps',
			sortable: true,
			align: 'center',
			render: (row) => (
				<span className="steps-badge">{row.steps_count || 0} steps</span>
			),
		},
		{
			key: 'projects_count',
			label: 'Projects',
			sortable: true,
			align: 'center',
			render: (row) => (
				<span className="projects-count">{row.projects_count || 0}</span>
			),
		},
		{
			key: 'status',
			label: 'Status',
			sortable: true,
			render: (row) => {
				const statusConfig = {
					active: { icon: CheckCircle, className: 'badge--success', label: 'Active' },
					disabled: { icon: XCircle, className: 'badge--warning', label: 'Disabled' },
					archived: { icon: Archive, className: 'badge--secondary', label: 'Archived' },
				}
				const config = statusConfig[row.status] || statusConfig.active
				const Icon = config.icon
				return (
					<span className={`badge ${config.className}`}>
						<Icon size={14} />
						{config.label}
					</span>
				)
			},
		},
		{
			key: 'created_at',
			label: 'Created',
			sortable: true,
			render: (row) => {
				const date = new Date(row.created_at)
				return (
					<div className="date-cell">
						<Clock size={14} />
						{date.toLocaleDateString()}
					</div>
				)
			},
		},
	]

	// Row actions
	const rowActions = [
		{
			label: 'View Details',
			icon: <Eye size={16} />,
			onClick: handleView,
			variant: 'default',
		},
		{
			label: 'Edit',
			icon: <Edit size={16} />,
			onClick: handleEdit,
			variant: 'default',
			show: (row) => canManage && row.status !== 'archived',
		},
		{
			label: (row) => (row.status === 'active' ? 'Disable' : 'Enable'),
			icon: (row) => (row.status === 'active' ? <ToggleLeft size={16} /> : <ToggleRight size={16} />),
			onClick: handleToggle,
			variant: (row) => (row.status === 'active' ? 'warning' : 'success'),
			show: (row) => canManage && row.status !== 'archived',
		},
		{
			label: (row) => (row.projects_count > 0 ? 'Archive' : 'Delete'),
			icon: (row) => (row.projects_count > 0 ? <Archive size={16} /> : <Trash2 size={16} />),
			onClick: handleDeleteClick,
			variant: 'danger',
			show: () => canDelete,
		},
	]

	// Stats cards data
	const statsCards = [
		{
			label: 'Total Workflows',
			value: stats.total,
			icon: GitBranch,
			iconClass: 'stat-card__icon--primary',
		},
		{
			label: 'Active',
			value: stats.active,
			icon: CheckCircle,
			iconClass: 'stat-card__icon--success',
		},
		{
			label: 'Disabled',
			value: stats.disabled,
			icon: XCircle,
			iconClass: 'stat-card__icon--warning',
		},
		{
			label: 'Archived',
			value: stats.archived,
			icon: Archive,
			iconClass: 'stat-card__icon--secondary',
		},
	]

	return (
		<div className="workflows-page">
			{/* Page Header */}
			<div className="page-header">
				<div className="page-header__content">
					<h1 className="page-header__title">
						<GitBranch size={28} />
						Workflows
					</h1>
					<p className="page-header__subtitle">
						Manage approval workflows for compliance checklist items
					</p>
				</div>
				{canManage && (
					<Button variant="primary" onClick={handleCreate}>
						<Plus size={18} />
						Create Workflow
					</Button>
				)}
			</div>

			{/* Stats Cards */}
			<div className="stats-grid">
				{statsCards.map((stat) => {
					const Icon = stat.icon
					return (
						<div key={stat.label} className="stat-card">
							<div className={`stat-card__icon ${stat.iconClass}`}>
								<Icon size={24} />
							</div>
							<div className="stat-card__content">
								<div className="stat-card__label">{stat.label}</div>
								<div className="stat-card__value">{stat.value}</div>
							</div>
						</div>
					)
				})}
			</div>

			{/* Filters */}
			<div className="filters-section">
				<div className="filters-section__search">
					<SearchBar
						placeholder="Search workflows..."
						value={filters.search}
						onChange={handleSearch}
						debounceMs={300}
					/>
				</div>
				<div className="filters-section__controls">
					<div className="filter-group">
						<Filter size={16} />
						<select value={filters.status} onChange={handleStatusChange}>
							<option value="all">All Status</option>
							<option value="active">Active</option>
							<option value="disabled">Disabled</option>
							<option value="archived">Archived</option>
						</select>
					</div>
				</div>
			</div>

			{/* Error Alert */}
			{error && (
				<Alert variant="error" message={error} className="workflows-alert" />
			)}

			{/* Data Table */}
			<div className="workflows-table-container">
				{loading ? (
					<div className="loading-state">
						<Loader2 className="spin" size={40} />
						<p>Loading workflows...</p>
					</div>
				) : workflows.length === 0 ? (
					<div className="empty-state">
						<GitBranch size={48} />
						<h3>No Workflows Found</h3>
						<p>
							{filters.search || filters.status !== 'all'
								? 'No workflows match your filters. Try adjusting your search criteria.'
								: 'Get started by creating your first approval workflow.'}
						</p>
						{canManage && !filters.search && filters.status === 'all' && (
							<Button variant="primary" onClick={handleCreate}>
								<Plus size={18} />
								Create First Workflow
							</Button>
						)}
					</div>
				) : (
					<DataTable
						columns={columns}
						data={workflows}
						actions={rowActions}
						emptyMessage="No workflows found"
						onRowClick={handleView}
					/>
				)}
			</div>

			{/* Delete/Archive Confirmation Modal */}
			<Modal
				isOpen={deleteModalOpen}
				onClose={() => setDeleteModalOpen(false)}
				title={selectedWorkflow?.projects_count > 0 ? 'Archive Workflow' : 'Delete Workflow'}
				size="small"
				closeOnBackdrop={false}
			>
				<div className="delete-modal">
					<div className="delete-modal__icon">
						{selectedWorkflow?.projects_count > 0 ? (
							<Archive size={48} />
						) : (
							<AlertCircle size={48} />
						)}
					</div>
					<div className="delete-modal__content">
						{selectedWorkflow?.projects_count > 0 ? (
							<>
								<p>
									This workflow is attached to <strong>{selectedWorkflow.projects_count} project(s)</strong>.
								</p>
								<p>It will be archived instead of deleted. Archived workflows cannot be assigned to new projects.</p>
							</>
						) : (
							<>
								<p>
									Are you sure you want to delete <strong>{selectedWorkflow?.name}</strong>?
								</p>
								<p>This action cannot be undone.</p>
							</>
						)}
					</div>
					<div className="delete-modal__actions">
						<Button variant="outline" onClick={() => setDeleteModalOpen(false)} disabled={actionLoading}>
							Cancel
						</Button>
						<Button variant="danger" onClick={handleDeleteConfirm} disabled={actionLoading}>
							{actionLoading ? (
								<>
									<Loader2 className="spin" size={16} />
									Processing...
								</>
							) : selectedWorkflow?.projects_count > 0 ? (
								'Archive'
							) : (
								'Delete'
							)}
						</Button>
					</div>
				</div>
			</Modal>
		</div>
	)
}

export default Workflows
